import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, AlertCircle, Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useWizardStore, useWizardActions } from '@/stores';
import { useValidateOrganization } from '@/features/migrations/hooks';
import {
  existingOrganizationSchema,
  normalizeAmplifyOrganizationUuid,
  type ExistingOrganizationFormData,
} from '@/lib/validations';

interface OrganizationDetails {
  name: string;
  uuid: string;
  ownerName?: string | undefined;
  ownerEmail?: string | undefined;
  existingModules: string[];
}

export function ExistingOrganizationForm() {
  const organizationData = useWizardStore((s) => s.organizationData);
  const { setOrganizationData, setIsValid } = useWizardActions();
  const [validatedOrg, setValidatedOrg] = useState<OrganizationDetails | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid: formIsValid },
  } = useForm<ExistingOrganizationFormData>({
    resolver: zodResolver(existingOrganizationSchema),
    mode: 'onChange',
    defaultValues: {
      amplifyOrganizationUuid: organizationData?.amplifyOrganizationUuid ?? '',
    },
  });

  const { mutate: validateOrg, isPending, isError, error } = useValidateOrganization({
    onSuccess: (result) => {
      if (result.isValid) {
        const orgName = result.organizationName ?? normalizedUuid;
        setValidatedOrg({
          name: orgName,
          uuid: normalizedUuid,
          ownerName: result.ownerName,
          ownerEmail: result.ownerEmail,
          existingModules: result.existingModules ?? [],
        });
        setOrganizationData({
          organizationType: 'Existing',
          amplifyOrganizationUuid: normalizedUuid,
          organizationName: orgName,
        });
        setIsValid(true);
      } else {
        setValidatedOrg(null);
        setIsValid(false);
      }
    },
    onError: () => {
      setValidatedOrg(null);
      setIsValid(false);
    },
  });

  const uuid = watch('amplifyOrganizationUuid');
  // Only the canonical (trimmed, lowercase) form is validated, stored, or compared — a
  // case-only edit to an already-validated UUID is not a change.
  const normalizedUuid = normalizeAmplifyOrganizationUuid(uuid);

  // Reset validation when UUID changes
  useEffect(() => {
    if (validatedOrg && validatedOrg.uuid !== normalizedUuid) {
      setValidatedOrg(null);
      setIsValid(false);
    }
  }, [normalizedUuid, validatedOrg, setIsValid]);

  const onValidate = handleSubmit((data) => {
    validateOrg({
      amplifyOrganizationUuid: normalizeAmplifyOrganizationUuid(data.amplifyOrganizationUuid),
    });
  });

  return (
    <div className="space-y-6">
      <form onSubmit={onValidate} className="space-y-4">
        <div>
          <Label htmlFor="amplifyOrganizationUuid">
            Amplify Organization UUID <span className="text-destructive">*</span>
          </Label>
          <div className="flex gap-2 mt-1">
            <Input
              id="amplifyOrganizationUuid"
              {...register('amplifyOrganizationUuid')}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="font-mono"
              aria-invalid={!!errors.amplifyOrganizationUuid}
              aria-describedby={errors.amplifyOrganizationUuid ? 'uuid-error' : undefined}
            />
            <Button
              type="submit"
              disabled={!formIsValid || isPending}
              variant={validatedOrg ? 'secondary' : 'default'}
            >
              {isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Validate
                </>
              )}
            </Button>
          </div>
          {errors.amplifyOrganizationUuid && (
            <p id="uuid-error" className="text-sm text-destructive mt-1">
              {errors.amplifyOrganizationUuid.message}
            </p>
          )}
          <p className="text-sm text-muted-foreground mt-1">
            Enter the UUID of the existing Amplify organization you want to migrate data to.
          </p>
        </div>
      </form>

      {/* Validation Error */}
      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Validation Failed</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Unable to validate organization. Please check the UUID and try again.'}
          </AlertDescription>
        </Alert>
      )}

      {/* Validated Organization Details */}
      {validatedOrg && (
        <Card className="border-success">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-success" />
              <CardTitle className="text-lg">Organization Found</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <span className="text-sm text-muted-foreground">Name: </span>
              <span className="font-medium">{validatedOrg.name}</span>
            </div>
            <div>
              <span className="text-sm text-muted-foreground">UUID: </span>
              <code className="text-sm bg-muted px-1 rounded">{validatedOrg.uuid}</code>
            </div>
            {validatedOrg.ownerName && (
              <div>
                <span className="text-sm text-muted-foreground">Owner: </span>
                <span className="font-medium">{validatedOrg.ownerName}</span>
              </div>
            )}
            {validatedOrg.ownerEmail && (
              <div>
                <span className="text-sm text-muted-foreground">Email: </span>
                <span className="font-medium">{validatedOrg.ownerEmail}</span>
              </div>
            )}

            {validatedOrg.existingModules.length > 0 && (
              <Alert variant="warning" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Existing Modules</AlertTitle>
                <AlertDescription>
                  This organization already has the following modules:{' '}
                  {validatedOrg.existingModules.join(', ')}.
                  Migration may overwrite existing data.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
